import React from "react";
import { Card, CardContent, Typography, Box, Button, Chip } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { alpha, useTheme } from "@mui/material/styles";

const DeliverableCard = ({ deliverable, onViewOffer, isUserMessage, uiVariant = 'default', accentColor }) => {
  const theme = useTheme();
  const isPartnershipClean = uiVariant === 'partnership-clean';
  const primaryAccent = accentColor || theme.palette.primary.main;
  // Helper functions for date formatting
  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : "N/A";
  // Get status color based on deliverable status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
      case 'active':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Render milestones if available
  const renderMilestones = () => {
    if (!deliverable.milestones || deliverable.milestones.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No milestones defined.
        </Typography>
      );
    }
    return deliverable.milestones.map((milestone, idx) => (
      <Box
        key={idx}
        sx={{
          mt: 1.5,
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            borderColor: 'primary.light',
          },
        }}
      >
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
          {milestone.description}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {formatDate(milestone.due_date)}
            </Typography>
          </Box>
          {milestone.amount != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                ${parseFloat(milestone.amount).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
        {milestone.link && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Link:</strong>{" "}
            <a 
              href={milestone.link} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {milestone.link}
            </a>
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 0,
        minHeight: 'auto',
        width: isPartnershipClean ? { xs: 292, sm: 320 } : '100%',
        minWidth: isPartnershipClean ? 'unset' : { xs: 280, sm: 360 },
        maxWidth: isPartnershipClean ? 320 : 420,
        borderRadius: isPartnershipClean ? 3 : 2,
        boxShadow: isPartnershipClean ? '0 16px 32px rgba(15, 23, 42, 0.08)' : '0 2px 8px rgba(0,0,0,0.06)',
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        border: '1px solid',
        borderColor: isPartnershipClean ? alpha('#cbd5e1', 0.8) : 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        mx: isPartnershipClean ? 'auto' : 0,
      }}
    >
      <Box sx={{ p: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: isPartnershipClean ? '#eef3fb' : alpha(theme.palette.action.hover, 0.3) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: isPartnershipClean ? alpha(primaryAccent, 0.12) : 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocalOfferIcon sx={{ color: isPartnershipClean ? primaryAccent : 'text.secondary', fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary', textTransform: 'capitalize' }}>
                {deliverable.deliverable_type || "Offer"}
              </Typography>
              <Chip
                label={deliverable.status || 'accept'}
                color={getStatusColor(deliverable.status)}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2.5, pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachMoneyIcon sx={{ fontSize: 20, color: 'success.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              ${deliverable.amount ? parseFloat(deliverable.amount).toFixed(2) : "0.00"}
            </Typography>
          </Box>
          {deliverable.due_date && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Due: {formatDate(deliverable.due_date)}
              </Typography>
            </Box>
          )}
        </Box>

        {deliverable.platforms && deliverable.platforms.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              Platforms:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(Array.isArray(deliverable.platforms) ? deliverable.platforms : [deliverable.platforms]).map((platform, idx) => (
                <Chip 
                  key={idx}
                  label={platform} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Notes Section */}
        {deliverable.notes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.5 }}>
              Notes:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {deliverable.notes}
            </Typography>
          </Box>
        )}

        {deliverable.revision != null && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <strong>Revision:</strong> {deliverable.revision}
            </Typography>
          </Box>
        )}

        {deliverable.script && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Script:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {deliverable.script}
            </Typography>
          </Box>
        )}
        
        {/* Milestones if needed */}
        {deliverable.milestones && deliverable.milestones.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>
              MILESTONES ({deliverable.milestones.length})
            </Typography>
            {renderMilestones()}
          </Box>
        )}
      </CardContent>

      {/* Button Row */}
      <Box sx={{ p: 2, pt: 0, bgcolor: 'transparent' }}>
        <Button
          onClick={() => onViewOffer && onViewOffer(deliverable)}
          fullWidth
          variant="contained"
          startIcon={<LocalOfferIcon />}
          sx={{
            bgcolor: isPartnershipClean ? primaryAccent : '#0f172a',
            color: '#fff',
            textTransform: "none",
            borderRadius: isPartnershipClean ? 999 : 2,
            py: 1,
            fontWeight: 600,
            fontSize: '0.9rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: isPartnershipClean ? primaryAccent : '#1e293b',
              filter: isPartnershipClean ? 'brightness(0.96)' : 'none',
              boxShadow: 'none',
            },
            transition: 'all 0.2s ease',
          }}
        >
          View Offer
        </Button>
      </Box>
    </Card>
  );
};

export default DeliverableCard;
